import { useEffect, useState } from 'react'
import { LayoutChangeEvent, NativeScrollEvent, NativeSyntheticEvent } from 'react-native'

const LOAD_MORE_THRESHOLD = 60

interface UseLoadMoreOnScrollParams {
  hasNextPage: boolean
  isFetching: boolean
  isFetchingNextPage: boolean
  isFetchNextPageError: boolean
  fetchNextPage: () => unknown
}

// A list of people in a plain ScrollView only ever showed its first page:
// nothing asked for the following ones. They are asked for from here: near the
// bottom of the list, and for as long as the people do not fill it.
export const useLoadMoreOnScroll = ({
  hasNextPage,
  isFetching,
  isFetchingNextPage,
  isFetchNextPageError,
  fetchNextPage,
}: UseLoadMoreOnScrollParams) => {
  const [listHeight, setListHeight] = useState(0)
  const [contentHeight, setContentHeight] = useState(0)

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage()
  }

  const onScroll = ({ nativeEvent }: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { layoutMeasurement, contentOffset, contentSize } = nativeEvent
    const isNearBottom =
      layoutMeasurement.height + contentOffset.y >= contentSize.height - LOAD_MORE_THRESHOLD
    if (isNearBottom) loadMore()
  }

  const onListLayout = (e: LayoutChangeEvent) => setListHeight(e.nativeEvent.layout.height)
  const onContentLayout = (e: LayoutChangeEvent) => setContentHeight(e.nativeEvent.layout.height)

  // The people do not fill the list (tall window, or a picker laid out inside
  // the scroll of its page): nothing can be scrolled, so the next page is asked
  // for from here. Not while the list is being fetched (it would cut short a
  // refresh under way), and not after a page failed: it would be asked for
  // again and again for as long as the server is down. A scroll retries it.
  useEffect(() => {
    if (listHeight === 0 || !hasNextPage || isFetching || isFetchNextPageError) return
    if (contentHeight <= listHeight + LOAD_MORE_THRESHOLD) void fetchNextPage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listHeight, contentHeight, hasNextPage, isFetching, isFetchNextPageError])

  return { onScroll, onListLayout, onContentLayout }
}
